import { SalePrice, CompareAtPrice, formatMoney } from 'theme'
import { GWElement } from 'elements'
import { VariantProductCard } from 'product-card'
import { reduceObject, uid, renderBanner, parseGID, updateUrlParam, getBaseProductTitle, arrayToDict, toCamel } from 'utilities'

export class BundlePicker extends GWElement {
  constructor() {
    super()
    this.formId = this.getAttribute('form')
    this.submitButton = document.querySelector(`bundle-buttons[form="${this.formId}"] [type="submit"]`)
    this.bundleComponentStrategy = this.dataset.bundleComponentStrategy || 'all' 
    this.minQty = this.dataset.minQty || 1 
    this.generateId()
    this.loadBundle()

    this.addListener(document, 'bundle:submit', this.onSubmit)
    if (!this.staticPrice) {
      this.addListener(this, 'bundle:change', this.onBundleChange)
    }

    console.log("this bundle", this);
  }

  init() {
    super.init()
    this.discountMultiplier = 1 - (this.options.discount / 100)
  }

  defaults() {
    return {
      productId: null,
      price: null,
      compareAtPrice: null,
      discount: 0,
      name: null,
      url: null
    }
  }

  generateId() {
    this.bundleId = uid()
  }

  get components() {
    return [...this.querySelectorAll('bundle-component')]
  }

  get isModification() {
    return !!this.loadedBundleId
  }

  get loadedBundleId() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('bundle')
  }

  get returnUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('return_url')
  }

  get loadedBundleLineItem() {
    return document.querySelector('line-item-bundle[bundle-id="' + this.loadedBundleId + '"]')
  }

  get form() {
    return document.forms[this.getAttribute('form')]
  }

  get staticPrice() {
    return this.hasAttribute('static-price') && this.getAttribute('static-price') !== 'false'
  }

  getSubmitButtonText(valid) {
    const { strings } = window.themeVariables

    if (!valid) return strings.bundleSelectionRequired

    return this.isModification ? strings.bundleUpdate : strings.bundleAddToCart
  }

  async getLoadedBundleComponents() {
    const loadedBundleLineItem = this.loadedBundleLineItem
    if (loadedBundleLineItem) return loadedBundleLineItem.components

    const response = await fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'GET',
      headers: {
      'Content-Type': 'application/json'
      }
    })
    const cart = await response.json()
    const bundleItems = cart.items.filter(item => item.properties._bundle_id === this.loadedBundleId).map(item => {
      const { id, product_title, options_with_values } = item
      const title = getBaseProductTitle(product_title)
      const options = arrayToDict(options_with_values, { key: 'name', keyTransform: toCamel })

      return { id, title, options }
    })

    if (!bundleItems.length) {
      updateUrlParam('bundle', null)
    }

    return bundleItems
  }

  async loadBundle() {
    console.log('Loading bundle:', this.loadedBundleId);
    console.log('Is modification:', this.isModification);
    if (!this.isModification) return

    const loadedComponents = {}
    for (const { title, options } of await this.getLoadedBundleComponents()) {
      const componentIndex = loadedComponents[title] ?? 0

      const matchingComponents = [...this.querySelectorAll(`bundle-component[product-title="${title.replace(/"/g, '\\"')}"]`)]
      const component = matchingComponents[componentIndex]
      component.selections = options

      console.log('Loaded component:', title, options, componentIndex, matchingComponents.length)
      console.log('Loaded component selections:', component.selections)

      loadedComponents[title] = componentIndex + 1
    }
  }

  validateBundle() {
    let isValid = this.components.every(component => component.validateBundleComponent())
    if(this.bundleComponentStrategy === 'different-colors' || this.bundleComponentStrategy === 'same-color') {
      const selectedBundleColors = this.components.filter(component => component.validateBundleComponent() && component.validateBundleQuantity());
      if(selectedBundleColors) {
        const totalQuantity = selectedBundleColors.reduce(
          (sum, component) => sum + parseInt(component.quantity),
          0
        );
        isValid = totalQuantity >= this.minQty
      } else {
        isValid = false
      }
    }
    this.updateSubmitButton(isValid)

    return isValid
  }

  get selections() {
    if(this.bundleComponentStrategy === 'different-colors' || this.bundleComponentStrategy === 'same-color') {
      const thisComponents = this.components.filter(component => component.quantity >= 1)
                                          .map(component => ({
                                            ...component.data,
                                            quantity: component.quantity
                                          }));
      return thisComponents;
    } else {
      return this.components.map(component => component.data).reverse()
    }
  }

  updateSubmitButton(valid = false) {
    this.submitButton.disabled = !valid
    this.submitButton.setAttribute("aria-disabled", !valid);
    this.submitButton.textContent = this.getSubmitButtonText(valid)
  }

  discountPrice(price) {
    return Math.ceil(price * this.discountMultiplier)
  }

  onBundleChange(e) {
    const compareAtPrice = this.selections.reduce((total, variant) => total + parseInt(variant.price, 10), 0)
    const price = this.discountPrice(compareAtPrice)
    this.form.dispatchEvent(new CustomEvent('price:change', { detail: { price, compareAtPrice } }))
  }

  async onSubmit() {
    if (!this.validateBundle()) return


    const componentIDs = this.selections.map(({ id }) => id)

    console.log(this);

    console.log('this.selections', this.selections);
    const lineItems = this.selections.map((variant, index) => ({
      id: variant.id,
      quantity: (this.bundleComponentStrategy === 'different-colors'  || this.bundleComponentStrategy === 'same-color') ? parseInt(variant.quantity) : 1,
      properties: {
        _bundle_id: this.bundleId,
        _bundle_currency: Shopify.currency.active,
        _bundle_components: componentIDs,
        _bundle_component_count: this.selections.length,
        _bundle_component_position: index,
        _bundle_component_handle: variant.handle,
        _bundle_component_original_price: variant.price,
        _bundle_discount: this.options.discount,
        _bundle_compare_at_price: this.options.compareAtPrice,
        _bundle_url: this.options.url,
        _bundle_cost: this.options.price,
        _bundle_name: this.options.name,
        _bundle_strategy: this.bundleComponentStrategy
      }
    }))

    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }))
    let sectionsToBundle = []
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }))

    let cart = { items: [] }
    if (this.isModification) {
      const loadedBundleLineItem = this.loadedBundleLineItem

      if (loadedBundleLineItem) {
        cart = await loadedBundleLineItem.removeBundle()
        updateUrlParam('bundle', this.bundleId)
      }
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: lineItems,
        sections: sectionsToBundle.join(','),
        // TODO: Uncomment this if quick-add support is added to bundles
        // sections_url: `${window.Shopify.routes.root}variants/${this.options.productId}`
      }),
    })
    const data = await response.json()
    
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }))
    if (!response.ok) {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: data.description
        }
      }));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      return
    }

    const { sections } = data
    cart = { ...cart, items: [...cart.items, ...data.items], sections }
    cart.item_count = cart.items.length

    this.dispatchEvent(new CustomEvent("bundle:add", {
      bubbles: true,
      detail: { id: this.bundleId, isModification: this.isModification, items: data.hasOwnProperty("items") ? data["items"] : [data] }
    }));

    const returnUrl = this.returnUrl
    if (returnUrl) {
      window.location.href = returnUrl
    } else {
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          onSuccessDo: 'force_open_drawer',
          cart,
        }
      }));
    }

    if (!this.isModification) {
      this.generateId()
    }
  }
}
if (!window.customElements.get('bundle-picker')) {
  window.customElements.define('bundle-picker', BundlePicker)
}

/**
 * Bundle component element that extends product card functionality.
 * 
 * @extends VariantProductCard
 */
export class BundleComponent extends VariantProductCard {
  constructor() {
    super()

    const fieldsets = {
      size: this.querySelector('fieldset[data-option="size"]'),
      color: this.querySelector('fieldset[data-option="color"]')
    }
    this.quantityElement = this.querySelector('input.quantity-selector__input')

    const values = reduceObject(fieldsets, (acc, [key, fieldset]) => ({...acc, [key]: fieldset.querySelector('[data-option-value]')}))
    this.elements = {
      picker: this.closest('bundle-picker'),
      fieldsets,
      values,
    }
    this.addListener(this.elements.fieldsets.size, 'change', this.onSizeSelected.bind(this))

    if(this.quantityElement) {
      this.quantity = this.quantityElement.value || 1
      this.addListener(this.quantityElement, 'change', this.onQuantityChange.bind(this))
    }

    this.addAttributeListener('current-size', this.onSizeChange)
  }

  static get observedAttributes() {
    return ['current-color', 'current-size']
  }

  onSizeSelected(event) {
    this.size = event.target.checked ? event.target.value : null
  }

  onQuantityChange(event) {
    this.quantity = event.target.value
    this.elements.picker.validateBundle()
  }

  get size() {
    const size = this.getAttribute('current-size')

    if (size === 'null') return null
    return size
  }

  set size(value) {
    this.setAttribute('current-size', value)
  }

  onSizeChange(value, previousValue) {
    if (!value || value === previousValue) return

    this.sizeButtons[value].checked = true
    this.elements.values.size.textContent = value
    this.elements.picker.validateBundle()
  }

  get color() {
    return super.color
  }

  set color(value) {
    this.setAttribute('current-color', value)
  }

  get strategy() {
    return this.getAttribute('strategy') || 'all'
  }

  onColorChange(value, previousValue, attribute) {
    if (value === previousValue) return

    this.elements.values.color.textContent = value
    this.elements.picker.validateBundle()

    super.onColorChange(value, previousValue, attribute)

    if (this.product) {
      this.elements.picker.dispatchEvent(new CustomEvent("bundle:change", {
        bubbles: true,
        detail: { source: this, color: value, variant: { ...this.variant, id: parseGID(this.variant.id), price: Number(this.variant.price.amount) * 100, handle: this.product.handle } }
      }))
    }

    if (!this.size) this.elements.picker.validateBundle()
  }

  get variantId() {
    return this.sizeButtons[this.size]?.dataset.variantId
  }

  get variant() {
    if (!this.product) return null

    return this.variantId ? this.product.variants.find(variant => parseGID(variant.id) == this.variantId) : this.product.variants[0]
  }

  set selections({ color, size }) {
    if (!color || !size) return

    this.color = color
    this.size = size
  }

  get selections() {
    return this.variant?.options
  }

  get data() {
    if (!this.variant) return null

    return { ...this.variant, id: parseGID(this.variant.id), price: Number(this.variant.price.amount) * 100, handle: this.product.handle }
  }

  renderSwatches(data) {
    if (this.strategy === 'manual' || this.strategy == 'same-color' || this.strategy == 'different-colors') return

    super.renderSwatches(data)
  }

  validateBundleComponent() {
    return !!this.size && !!this.variantId
  }

  validateBundleQuantity() {
    return this.quantity > 0
  }
}
if (!window.customElements.get('bundle-component')) {
  window.customElements.define('bundle-component', BundleComponent)
}

export class BundleButtons extends GWElement {
  constructor() {
    super()

    this.submitButton = this.querySelector('[type="submit"]')
    this.addListener(this.submitButton, 'click', this.onSubmit)
    this.addListener(document, 'bundle:add', this.onBundleAdded)
    this.addListener(document, 'cart:error', this.onCartError)
  }

  onSubmit(event) {
    event.preventDefault()

    this.dispatchEvent(new CustomEvent('bundle:submit', { bubbles: true }))
  }

  onBundleAdded(e) {
    const { bundleAddedToCart, bundleUpdated } = window.themeVariables.strings
    const message = (e.detail.isModification) ? bundleUpdated : bundleAddedToCart

    renderBanner(message, 'success', {
      callback: this.prepend.bind(this)
    })
  }

  onCartError(event) {
    renderBanner(event.detail.error, 'error', {
      callback: this.prepend.bind(this)
    });
  }
}
if (!window.customElements.get('bundle-buttons')) {
  window.customElements.define('bundle-buttons', BundleButtons)
}

const currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat
export class BundleSalePrice extends SalePrice {
  constructor() {
    super()
    this.handlePriceChange = this.onPriceChange.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.form?.addEventListener('price:change', this.handlePriceChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.form?.removeEventListener('price:change', this.handlePriceChange)
  }

  get form() {
    return document.forms[this.getAttribute('form')]
  }

  onPriceChange(event) {
    const { price, compareAtPrice } = event.detail
    this.lastChild.replaceWith(document.createRange().createContextualFragment(formatMoney(price, currencyFormat)));
    this.classList.toggle("text-on-sale", compareAtPrice > price)
  }
}
if (!window.customElements.get('bundle-sale-price')) {
  window.customElements.define('bundle-sale-price', BundleSalePrice)
}

export class BundleCompareAtPrice extends CompareAtPrice {
  constructor() {
    super()
    this.handlePriceChange = this.onPriceChange.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.form?.addEventListener('price:change', this.handlePriceChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.form?.removeEventListener('price:change', this.handlePriceChange)
  }

  get form() {
    return document.forms[this.getAttribute('form')]
  }

  onPriceChange(event) {
    const { price, compareAtPrice } = event.detail
    this.lastChild.replaceWith(document.createRange().createContextualFragment(formatMoney(compareAtPrice, currencyFormat)));
    this.hidden = !(compareAtPrice > price);
  }
}
if (!window.customElements.get('bundle-compare-at-price')) {
  window.customElements.define('bundle-compare-at-price', BundleCompareAtPrice)
}